import { useCallback, useEffect, useState } from 'react';
import {
  BottomSheetContainer,
  ColorValues,
  Flex,
  NaviIconClose,
  RemoteImage,
  Spinner,
  Typography,
  createUseBottomSheetModal,
} from '@banhala/ui-kit';
import { useGetFeedbackTitles } from 'shared/src/hooks/useFeedback';
import { useSaveFeedback } from 'shared/src/hooks/useFeedback';
import type {
  FeedbackCacheItem,
  FeedbackItem,
  FeedbackType,
} from 'shared/src/api/feedbackService/types';
import type { IScreensLogging } from 'shared/src/api/types';
import { renderHTMLToText } from 'shared/src/utils/parseHTMLToReactNative';
import {
  useLogClickFeedback,
  useLogViewFeedbackModal,
} from './hooks';

type FeedbackModalParam = {
  goodsSno: number;
  feedbackType: FeedbackType;
  logging?: IScreensLogging;
  screen?: string;
};

// 피드백 아이템 행
const FeedbackItemRow = ({
  item,
  onPress,
  showDivider,
}: {
  item: FeedbackItem;
  onPress: () => void;
  showDivider: boolean;
}) => {
  return (
    <>
      <Flex.Row
        padding={{ vertical: 16, horizontal: 16 }}
        alignItems="center"
        onPress={onPress}
      >
        {item.icon_url && (
          <Flex margin={{ right: 8 }}>
            <RemoteImage uri={item.icon_url} width={24} height={24} />
          </Flex>
        )}
        <Flex flex={1}>
          {renderHTMLToText(item.message, {
            style: { fontSize: 16, lineHeight: 20, letterSpacing: -0.4, color: ColorValues.gray70 },
          })}
        </Flex>
      </Flex.Row>
      {showDivider && (
        <Flex
          margin={{ horizontal: 16 }}
          height={1}
          backgroundColor="bg_secondary"
        />
      )}
    </>
  );
};

// 상품 정보 헤더
const FeedbackGoodsHeader = ({
  name,
  marketName,
  imageUrl,
}: {
  name: string | null;
  marketName: string | null;
  imageUrl: string | null;
}) => {
  return (
    <Flex.Row padding={{ top: 16, horizontal: 16 }} alignItems="center">
      {imageUrl && (
        <Flex
          margin={{ right: 12 }}
          width={60}
          height={60}
          overflow="hidden"
          borderRadius="small"
          backgroundColor="bg_secondary"
        >
          <RemoteImage uri={imageUrl} width={60} height={60} />
        </Flex>
      )}
      <Flex flex={1}>
        {name && (
          <Typography
            type="subtitle2"
            color="content_primary"
            numberOfLines={1}
          >
            {name}
          </Typography>
        )}
        {marketName && (
          <Flex margin={{ top: 4 }}>
            <Typography
              type="body4"
              color="content_secondary"
              numberOfLines={1}
            >
              {marketName}
            </Typography>
          </Flex>
        )}
      </Flex>
    </Flex.Row>
  );
};

// 롱프레스 가이드 배너
const LongPressGuide = ({ onClose }: { onClose: () => void }) => {
  return (
    <Flex
      margin={{ horizontal: 16 }}
      height={50}
      borderRadius="medium"
      backgroundColor="bg_secondary"
    >
      <Flex.Row flex={1} alignItems="center" justifyContent="space-between">
        <Flex flex={1} margin={{ left: 14 }}>
          <Typography type="body4" color="content_primary">
            상품 카드를 길게 누르면 똑같은 기능을 사용할 수 있어요!
          </Typography>
        </Flex>
        <Flex
          margin={{ right: 12 }}
          width={40}
          height={40}
          alignItems="center"
          justifyContent="center"
          onPress={onClose}
        >
          <NaviIconClose fill={ColorValues.gray50} width={16} height={16} />
        </Flex>
      </Flex.Row>
    </Flex>
  );
};

export const useGoodsCardFeedbackModal = createUseBottomSheetModal<
  FeedbackCacheItem,
  FeedbackModalParam
>(({ confirm, cancel, param }) => {
  const { goodsSno, feedbackType, logging, screen } = param;
  const [showGuide, setShowGuide] = useState(true);

  const { data: feedbackData, isLoading, isError } = useGetFeedbackTitles({
    feedbackType,
    goodsSno,
    enabled: true,
  });

  const { mutateAsync: saveFeedback } = useSaveFeedback();
  const logViewFeedbackModal = useLogViewFeedbackModal(screen);
  const logClickFeedback = useLogClickFeedback(screen);

  // VIEW_FEEDBACK_MODAL 로깅
  useEffect(() => {
    logViewFeedbackModal({ logging });
  }, [logging, logViewFeedbackModal]);

  // API 실패 또는 빈 응답이면 모달 닫기 (render 중 side effect 방지)
  useEffect(() => {
    if (isError || (feedbackData && feedbackData.feedbacks.length === 0)) {
      cancel();
    }
  }, [isError, feedbackData, cancel]);

  const handleSelectFeedback = useCallback(
    async (item: FeedbackItem) => {
      logClickFeedback({
        logging,
        feedbackEventKey: item.event_key,
      });

      try {
        const response = await saveFeedback({
          goodsSno,
          feedbackSno: item.sno,
        });
        const cacheItem: FeedbackCacheItem = {
          goodsSno,
          feedbackResultSno: response.data.sno,
          feedbackItem: item,
        };
        confirm(cacheItem);
      } catch {
        cancel();
      }
    },
    [goodsSno, logging, logClickFeedback, saveFeedback, confirm, cancel],
  );

  if (isLoading || !feedbackData) {
    return (
      <BottomSheetContainer
        showHandleBar
        hideBorder
        bottomDocked={{
          paired: false,
          button: { title: '닫기', onPress: cancel, outline: true },
          color: 'gray',
        }}
      >
        <Flex height={200} alignItems="center" justifyContent="center">
          <Spinner />
        </Flex>
      </BottomSheetContainer>
    );
  }

  // 빈 응답은 useEffect에서 cancel() 처리하므로 null 렌더
  if (feedbackData.feedbacks.length === 0) {
    return null;
  }

  return (
    <BottomSheetContainer
      showHandleBar
      hideBorder
      bottomDocked={{
        paired: false,
        button: { title: '닫기', onPress: cancel, outline: true },
        color: 'gray',
      }}
    >
      <Flex>
        {/* 상품 정보 표시 (goods 데이터가 있으면 노출) */}
        {feedbackData.goods && (
          <Flex margin={{ bottom: 10 }}>
            <FeedbackGoodsHeader
              name={feedbackData.goods.name}
              marketName={feedbackData.goods.market_name}
              imageUrl={feedbackData.goods.image_url}
            />
          </Flex>
        )}

        {/* 피드백 옵션 리스트 */}
        <Flex>
          {feedbackData.feedbacks.map((item, index) => (
            <FeedbackItemRow
              key={item.sno}
              item={item}
              onPress={() => handleSelectFeedback(item)}
              showDivider={
                feedbackData.goods != null &&
                index < feedbackData.feedbacks.length - 1
              }
            />
          ))}
        </Flex>

        {/* 롱프레스 가이드 */}
        {showGuide && (
          <LongPressGuide onClose={() => setShowGuide(false)} />
        )}
      </Flex>
    </BottomSheetContainer>
  );
});
